import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import ChatPage from "@/pages/ChatPage";
import NotFound from "./pages/NotFound";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const queryClient = new QueryClient();

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

  const handleNewChat = () => {
    setIsChatStarted(false);
    setSelectedChatId(null);
  };

  const handleSelectChat = (chatId: string) => {
    setSelectedChatId(chatId);
    setIsChatStarted(true);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <div className="h-screen w-screen overflow-hidden relative">
              {/* Video Background */}
              <video 
                autoPlay 
                loop 
                muted 
                className="absolute inset-0 w-full h-full object-cover z-0" 
                style={{ filter: 'brightness(0.7)' }}
                src="/videobg.mp4"
              />
              
              {/* Back button - only visible when chat is started */}
              {isChatStarted && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={handleNewChat}
                  className="absolute top-4 left-16 z-50 p-2 rounded-full bg-black/30 backdrop-blur-md border border-white/10 
                  hover:bg-black/40 transition-all duration-200 text-white"
                  style={{ boxShadow: '0 0 15px rgba(138, 43, 226, 0.2)' }}
                >
                  <ArrowLeft size={20} />
                </motion.button>
              )}
              
              <div className="h-full w-full flex items-center justify-center z-10">
                <Sidebar 
                  isSidebarOpen={isSidebarOpen} 
                  toggleSidebar={toggleSidebar}
                  onNewChat={handleNewChat}
                  onSelectChat={handleSelectChat}
                />
                <motion.main 
                  className="w-full h-full flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7 }}
                >
                  <Routes>
                    <Route path="/" element={
                      <ChatPage 
                        selectedChatId={selectedChatId}
                        isChatStarted={isChatStarted}
                        setIsChatStarted={setIsChatStarted}
                      />
                    } />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </motion.main>
              </div>
              
              {/* Footer with glassmorphism effect - more transparent */}
              <footer className="w-full p-3 text-center text-xs text-white/70 absolute bottom-0 z-50 backdrop-blur-sm bg-black/10 border-t border-white/10">
                <img src="/powered-by.png" alt="Powered by ObsidianAI+" className="h-10 mx-auto" />
              </footer>
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
